// src/pages/TransportOrderPage.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, ArrowLeft } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import { getProtected, postProtected } from '../../network/ApiService';
import { API_END_POINTS } from '../../network/apiEndPoint';
import TransportOrderForm from './TransportOrderForm';

const TransportOrderPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [assetData, setAssetData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssetData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const response = await getProtected(`${API_END_POINTS.transportOrder}${id}/`);
        setAssetData(response);
      } catch (error) {
        console.error('Error fetching asset data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAssetData();
  }, [id]);

  const exportToPDF = async () => {
    const content = contentRef.current;
    if (!content || exportingPDF) return;

    // 1) Switch to export mode so the form renders static text
    setExportingPDF(true);

    // 2) Let the DOM update
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      // 3) Render content to canvas
      const canvas = await html2canvas(content, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: content.scrollWidth,
        windowHeight: content.scrollHeight,
      });

      // 4) Build PDF from canvas
      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      const pageHeight = pdf.internal.pageSize.getHeight();

      let heightLeft = pdfHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;
      }

      const timestamp = new Date().toISOString()
      .replace(/[-:]/g, '')
      .replace('T', '_')
      .slice(0, 15);

      const downloadName = `Transport-order-${assetData?.serial_no || id}_${timestamp}.pdf`;
      pdf.save(downloadName);

      const blob = pdf.output('blob'); 
      const form = new FormData();

      form.append('attachment', blob, downloadName);
      form.append('asset_id', String(id));

      await postProtected(`${API_END_POINTS.transportOrder}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });


    } catch (error) {
      console.error('Error generating/uploading PDF:', error);
      alert('Error generating or uploading PDF. Please try again.');
    } finally {
      // 7) Exit export mode to restore interactive form
      setExportingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen p-4 sm:p-8">
      <div className="max-w-5xl mx-auto">
        <header className="flex justify-between items-center mb-6">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2">
            <ArrowLeft size={20} /> Back
          </button>
          <button
            onClick={exportToPDF}
            disabled={exportingPDF}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-2"
          >
            <Download size={20} />
            {exportingPDF ? 'Exporting...' : 'Export PDF'}
          </button>
        </header>

        <main ref={contentRef} className="bg-white p-6 shadow-lg">
          {assetData ? (
            <TransportOrderForm assetData={assetData} isExporting={exportingPDF} />
          ) : (
            <p>No asset data available.</p>
          )}
        </main>
      </div>
    </div>
  );
};

export default TransportOrderPage;